/**
 * Enda vägen mellan React-appen och Hono-API:et. Lägg inte `fetch` direkt i
 * komponenter — då blir felhanteringen inkonsekvent.
 */

import type {
  AdminUser,
  Employee,
  EmployeeBalance,
  ExportResult,
  FortnoxEmployee,
  FortnoxStatus,
  PendingExport,
  PunchAction,
  Shift,
  VacationBalance,
  VacationKind,
  VacationRequest,
  VacationStatus,
} from "#shared/types";

/** Kastas vid alla API-fel; `message` är redan på svenska och kan visas rakt av. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`/api${path}`, {
      credentials: "same-origin",
      ...init,
      headers: {
        ...(init?.body && !(init.body instanceof Blob)
          ? { "Content-Type": "application/json" }
          : {}),
        ...init?.headers,
      },
    });
  } catch {
    throw new ApiError(0, "Ingen kontakt med servern. Kontrollera nätverket.");
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const data = text ? safeJson(text) : null;

  if (!response.ok) {
    const message =
      (data && typeof data === "object" && "error" in data && typeof data.error === "string"
        ? data.error
        : null) ?? `Servern svarade ${response.status}.`;
    throw new ApiError(response.status, message);
  }

  return data as T;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

const json = (body: unknown) => JSON.stringify(body);

/* -------------------------------------------------------------------------- */
/* Panelen                                                                     */
/* -------------------------------------------------------------------------- */

export const panelApi = {
  employees: () => request<Employee[]>("/employees"),

  /** Cachade flex- och kompsaldon från Fortnox. */
  balances: () => request<EmployeeBalance[]>("/balances"),

  shiftsForDate: (date: string) => request<Shift[]>(`/shifts?date=${date}`),

  /** Pass före `before` som aldrig stämplades ut. */
  openShiftsBefore: (before: string) => request<Shift[]>(`/shifts/open?before=${before}`),

  punch: (employeeId: string, action: PunchAction, date: string) =>
    request<Shift>("/punch", {
      method: "POST",
      body: json({ employee_id: employeeId, action, date }),
    }),

  vacationFor: (employeeId: string) =>
    request<VacationRequest[]>(`/vacation?employee_id=${encodeURIComponent(employeeId)}`),

  applyForVacation: (input: {
    employee_id: string;
    start_date: string;
    end_date: string;
    kind: VacationKind;
    message: string | null;
  }) => request<VacationRequest>("/vacation", { method: "POST", body: json(input) }),

  withdrawVacation: (id: string, employeeId: string) =>
    request<{ ok: true }>(`/vacation/${id}/withdraw`, {
      method: "POST",
      body: json({ employee_id: employeeId }),
    }),
};

/* -------------------------------------------------------------------------- */
/* Inloggning                                                                  */
/* -------------------------------------------------------------------------- */

export const authApi = {
  me: () => request<{ admin: AdminUser | null }>("/auth/me"),

  login: (email: string, password: string) =>
    request<AdminUser>("/auth/login", { method: "POST", body: json({ email, password }) }),

  logout: () => request<{ ok: true }>("/auth/logout", { method: "POST" }),

  changePassword: (currentPassword: string, newPassword: string) =>
    request<{ ok: true }>("/auth/password", {
      method: "POST",
      body: json({ current_password: currentPassword, new_password: newPassword }),
    }),
};

/* -------------------------------------------------------------------------- */
/* Adminläget                                                                  */
/* -------------------------------------------------------------------------- */

export type EmployeeInput = {
  name: string;
  employee_number: string | null;
  active: boolean;
  sort_order: number;
  vacation_days_per_year: number;
};

export type ShiftPatch = {
  check_in: string;
  lunch_start: string;
  lunch_end: string;
  check_out: string;
  note: string;
};

export type ShiftCreate = ShiftPatch & {
  employee_id: string;
  work_date: string;
};

export const adminApi = {
  employees: () => request<Employee[]>("/admin/employees"),

  createEmployee: (input: EmployeeInput) =>
    request<Employee>("/admin/employees", { method: "POST", body: json(input) }),

  updateEmployee: (id: string, input: Partial<EmployeeInput>) =>
    request<Employee>(`/admin/employees/${id}`, { method: "PATCH", body: json(input) }),

  deleteEmployee: (id: string) =>
    request<{ ok: true }>(`/admin/employees/${id}`, { method: "DELETE" }),

  uploadPhoto: (id: string, photo: Blob) =>
    request<Employee>(`/admin/employees/${id}/photo`, {
      method: "PUT",
      body: photo,
      headers: { "Content-Type": photo.type || "image/jpeg" },
    }),

  deletePhoto: (id: string) =>
    request<Employee>(`/admin/employees/${id}/photo`, { method: "DELETE" }),

  shifts: (from: string, to: string) => request<Shift[]>(`/admin/shifts?from=${from}&to=${to}`),

  updateShift: (id: string, patch: ShiftPatch) =>
    request<Shift>(
      // Workers kör i UTC, så panelens tidszon måste följa med.
      `/admin/shifts/${id}?tz_offset=${new Date().getTimezoneOffset()}`,
      { method: "PATCH", body: json(patch) },
    ),

  createShift: (shift: ShiftCreate) =>
    request<Shift>(`/admin/shifts?tz_offset=${new Date().getTimezoneOffset()}`, {
      method: "POST",
      body: json(shift),
    }),

  deleteShift: (id: string) => request<{ ok: true }>(`/admin/shifts/${id}`, { method: "DELETE" }),

  vacation: (status?: VacationStatus) =>
    request<VacationRequest[]>(`/admin/vacation${status ? `?status=${status}` : ""}`),

  vacationBalances: (year: number) =>
    request<VacationBalance[]>(`/admin/vacation/balances?year=${year}`),

  decideVacation: (id: string, status: "godkand" | "nekad", note: string | null) =>
    request<VacationRequest>(`/admin/vacation/${id}/decision`, {
      method: "POST",
      body: json({ status, note }),
    }),

  /** Allt oskickat, oavsett datumfilter. */
  pendingExport: () => request<PendingExport>("/admin/export/pending"),

  /** Närvarokoden sitter på den anställda och skickas därför inte med här. */
  exportAttendance: (from: string, to: string) =>
    request<ExportResult & { skipped_missing_number: string[]; balance_error?: string }>(
      "/admin/export/attendance",
      { method: "POST", body: json({ from, to }) },
    ),

  exportVacation: (from: string, to: string) =>
    request<ExportResult & { days_sent: number; skipped_missing_number: string[] }>(
      "/admin/export/vacation",
      { method: "POST", body: json({ from, to }) },
    ),

  admins: () => request<AdminUser[]>("/admin/admins"),

  createAdmin: (input: { name: string; email: string; password: string }) =>
    request<AdminUser>("/admin/admins", { method: "POST", body: json(input) }),

  deleteAdmin: (id: string) => request<{ ok: true }>(`/admin/admins/${id}`, { method: "DELETE" }),

  resetAdminPassword: (id: string, password: string) =>
    request<{ ok: true }>(`/admin/admins/${id}/password`, {
      method: "POST",
      body: json({ password }),
    }),
};

/* -------------------------------------------------------------------------- */
/* Fortnox                                                                     */
/* -------------------------------------------------------------------------- */

export const fortnoxApi = {
  status: (test = false) => request<FortnoxStatus>(`/fortnox/status${test ? "?test=1" : ""}`),

  /** Anställda i Fortnox, för att koppla ihop anställningsnummer. */
  employees: () => request<FortnoxEmployee[]>("/fortnox/employees"),

  /** Hämtar om saldona från Fortnox och uppdaterar cachen. */
  refreshBalances: () =>
    request<EmployeeBalance[]>("/fortnox/balances/refresh", { method: "POST" }),

  /** OAuth-flödet måste ske i webbläsarfönstret, inte via fetch. */
  connectUrl: () => "/api/fortnox/connect",

  disconnect: () => request<{ ok: true }>("/fortnox/disconnect", { method: "POST" }),
};
